import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import debounce from 'lodash.debounce';
import './RouteControls.css';
import { MAP_CONSTANTS, US_STATES } from './mapConstants';
import { validateSearchResponse } from './mapUtils';
import { getCurrentUser } from '../../services/authService';

const isDeveloperUser = () => {
  try {
    const user = getCurrentUser();
    return user?.userType === 'dev';
  } catch (error) {
    console.error('Error checking developer status:', error);
    return false;
  }
};

const RouteControls = ({
  stops,
  routeData,
  draggedIndex,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDeleteStop,
  onAddStop,
  onUpdateStop,
  onSearchLocation,
  isDarkMode,
}) => {
  const [inputValues, setInputValues] = useState({});
  const [searchResults, setSearchResults] = useState({});
  const [activeInput, setActiveInput] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [showAddInput, setShowAddInput] = useState(false);
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [isAddSearching, setIsAddSearching] = useState(false);
  const inputRefs = useRef([]);
  const addInputRef = useRef(null);
  const originalValues = useRef({});
  const abortControllersRef = useRef({});

  useEffect(() => {
    const newValues = {};
    stops.forEach((stop, index) => {
      if (activeInput !== index) {
        newValues[index] = stop.name;
      }
    });
    setInputValues((prev) => ({ ...prev, ...newValues }));
  }, [stops, activeInput]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest('.ll-route-search-results') &&
        !event.target.closest('.ll-route-stop-input') &&
        !event.target.closest('.ll-route-search-input')
      ) {
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const updatePosition = () => {
      let targetElement = null;

      if (
        activeInput !== null &&
        activeInput !== 'add' &&
        inputRefs.current[activeInput]
      ) {
        targetElement = inputRefs.current[activeInput];
      } else if (activeInput === 'add' && addInputRef.current) {
        targetElement = addInputRef.current;
      }

      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    if (activeInput !== null) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [activeInput]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query, index, abortSignal) => {
        try {
          const results = await onSearchLocation(query, abortSignal);
          if (!abortSignal.aborted) {
            const validatedData = validateSearchResponse(results);
            setSearchResults((prev) => ({ ...prev, [index]: validatedData.data || [] }));
          }
        } catch (error) {
          if (error.name !== 'AbortError' && !abortSignal.aborted) {
            console.error('Search error:', error);
            setSearchResults((prev) => ({ ...prev, [index]: [] }));
          }
        }
      }, MAP_CONSTANTS.SEARCH_DEBOUNCE_MS),
    [onSearchLocation]
  );

  useEffect(() => {
    if (activeInput === null || activeInput === 'add') return;

    const query = inputValues[activeInput] || '';
    if (query.length < MAP_CONSTANTS.MIN_SEARCH_QUERY_LENGTH) {
      setSearchResults((prev) => ({ ...prev, [activeInput]: [] }));
      return;
    }

    if (abortControllersRef.current[activeInput]) {
      abortControllersRef.current[activeInput].abort();
    }

    const abortController = new AbortController();
    abortControllersRef.current[activeInput] = abortController;

    debouncedSearch(query, activeInput, abortController.signal);

    return () => {
      debouncedSearch.cancel();
      if (abortControllersRef.current[activeInput]) {
        abortControllersRef.current[activeInput].abort();
        delete abortControllersRef.current[activeInput];
      }
    };
  }, [activeInput, inputValues, debouncedSearch]);

  const debouncedAddSearch = useMemo(
    () =>
      debounce(async (query, abortSignal) => {
        setIsAddSearching(true);
        try {
          const results = await onSearchLocation(query, abortSignal);
          if (!abortSignal.aborted) {
            const validatedData = validateSearchResponse(results);
            setAddSearchResults(validatedData.data || []);
          }
        } catch (error) {
          if (error.name !== 'AbortError' && !abortSignal.aborted) {
            console.error('Search error:', error);
            setAddSearchResults([]);
          }
        } finally {
          if (!abortSignal.aborted) {
            setIsAddSearching(false);
          }
        }
      }, MAP_CONSTANTS.SEARCH_DEBOUNCE_MS),
    [onSearchLocation]
  );

  useEffect(() => {
    if (addSearchQuery.length < MAP_CONSTANTS.MIN_SEARCH_QUERY_LENGTH) {
      setAddSearchResults([]);
      return;
    }

    if (abortControllersRef.current.add) {
      abortControllersRef.current.add.abort();
    }

    const abortController = new AbortController();
    abortControllersRef.current.add = abortController;

    debouncedAddSearch(addSearchQuery, abortController.signal);

    return () => {
      debouncedAddSearch.cancel();
      if (abortControllersRef.current.add) {
        abortControllersRef.current.add.abort();
        delete abortControllersRef.current.add;
      }
    };
  }, [addSearchQuery, debouncedAddSearch]);

  const formatLocationDisplay = (result) => {
    const address = result.address || {};
    const city =
      address.city || address.town || address.village || address.hamlet;
    const state = address.state;

    const stateAbbr = getStateAbbreviation(state);

    if (city && stateAbbr) {
      return `${city}, ${stateAbbr}`;
    }

    return result.display_name || result.name;
  };

  const getStateAbbreviation = useCallback((stateName) => {
    if (!stateName) return null;
    return US_STATES[stateName] || stateName;
  }, []);

  const handleStopFocus = useCallback((index) => {
    setActiveInput(index);
    originalValues.current[index] = stops[index].name;
  }, [stops]);

  const handleStopBlur = useCallback((index) => {
    setTimeout(() => {
      setActiveInput((current) => {
        if (current === index) {
          setInputValues((prev) => ({
            ...prev,
            [index]: originalValues.current[index] || stops[index]?.name || '',
          }));
          return null;
        }
        return current;
      });
    }, MAP_CONSTANTS.BLUR_DELAY_MS);
  }, [stops]);

  const handleStopChange = useCallback((index, value) => {
    setInputValues((prev) => ({ ...prev, [index]: value }));
  }, []);

  const handleSelectLocation = useCallback((result, index) => {
    const lat = parseFloat(result?.lat)
    const lon = parseFloat(result?.lon)

    if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      console.error('Invalid coordinates from search result')
      return
    }

    const displayName = formatLocationDisplay(result);

    if (index === 'add') {
      onAddStop({
        id: Date.now(),
        name: displayName,
        coordinates: [lat, lon],
      });
      setAddSearchQuery('');
      setShowAddInput(false);
    } else {
      onUpdateStop(index, {
        name: displayName,
        coordinates: [lat, lon],
      });
      setInputValues((prev) => ({ ...prev, [index]: displayName }));
      originalValues.current[index] = displayName;
    }

    setActiveInput(null);
  }, [onAddStop, onUpdateStop]);

  const handleCancelAdd = useCallback(() => {
    setAddSearchQuery('');
    setAddSearchResults([]);
    setShowAddInput(false);
    setActiveInput(null);
  }, []);

  return (
    <div className={`ll-route-controls ${isDarkMode ? 'll-route-controls-dark' : ''}`}>
      <div className="ll-route-controls-list">
        {stops.map((stop, index) => (
          <div
            key={stop.id}
            className={`ll-route-stop-item ${
              draggedIndex === index ? 'll-route-stop-dragging' : ''
            }`}
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={(e) => onDragOver(e, index)}
            onDragEnd={onDragEnd}
          >
            <div className="ll-route-stop-drag-handle">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </div>
            <div className={`ll-route-stop-marker ${
              index === 0
                ? 'll-route-stop-marker-start'
                : index === stops.length - 1
                ? 'll-route-stop-marker-end'
                : ''
            }`}>
              {index + 1}
            </div>
            <div className="ll-route-stop-input-wrapper">
              <input
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                className="ll-route-stop-input"
                value={inputValues[index] ?? stop.name}
                onFocus={() => handleStopFocus(index)}
                onBlur={() => handleStopBlur(index)}
                onChange={(e) => handleStopChange(index, e.target.value)}
                placeholder={
                  index === 0
                    ? 'Choose starting point'
                    : index === stops.length - 1
                    ? 'Choose destination'
                    : `Stop ${index + 1}`
                }
              />
            </div>
            <button
              className="ll-route-stop-delete"
              onClick={() => onDeleteStop(index)}
              title="Delete stop"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        ))}

        {/* Autocomplete dropdown for stop inputs */}
        {activeInput !== null &&
          activeInput !== 'add' &&
          searchResults[activeInput] &&
          searchResults[activeInput].length > 0 && (
            <div
              className="ll-route-search-results"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
            >
              {searchResults[activeInput].slice(0, MAP_CONSTANTS.MAX_SEARCH_RESULTS).map((result) => (
                <div
                  key={`${result.lat}-${result.lon}-${result.display_name}`}
                  className="ll-route-search-result-item"
                  onMouseDown={() => handleSelectLocation(result, activeInput)}
                >
                  <div className="ll-route-search-result-name">
                    {formatLocationDisplay(result)}
                  </div>
                </div>
              ))}
            </div>
          )}

        {/* Autocomplete dropdown for add input */}
        {activeInput === 'add' && addSearchResults.length > 0 && (
          <div
            className="ll-route-search-results"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            {addSearchResults.slice(0, MAP_CONSTANTS.MAX_SEARCH_RESULTS).map((result) => (
              <div
                key={`${result.lat}-${result.lon}-${result.display_name}`}
                className="ll-route-search-result-item"
                onMouseDown={() => handleSelectLocation(result, 'add')}
              >
                <div className="ll-route-search-result-name">
                  {formatLocationDisplay(result)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Destination Section - inside scrollable list */}
        <div className="ll-route-add-section">
          {!showAddInput ? (
            <button
              className="ll-route-add-button"
              onClick={() => {
                setShowAddInput(true);
                setActiveInput('add');
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              Add Destination
            </button>
          ) : (
            <div className="ll-route-search-container">
              <div className="ll-route-search">
                <input
                  ref={addInputRef}
                  type="text"
                  placeholder="Search for a city..."
                  value={addSearchQuery}
                  onChange={(e) => setAddSearchQuery(e.target.value)}
                  onFocus={() => setActiveInput('add')}
                  className="ll-route-search-input"
                  autoFocus
                />
                {isAddSearching && (
                  <div className="ll-route-search-loading">Searching...</div>
                )}
              </div>
              <button
                className="ll-route-search-cancel"
                onClick={handleCancelAdd}
                title="Cancel"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Route Info under stops */}
        {routeData && routeData.route && routeData.route.trip && routeData.route.trip.legs?.length > 0 && (
          <div className="ll-route-info-bottom">
            {(() => {
              const totalDistance = routeData.route.trip.legs.reduce(
                (sum, leg) => sum + leg.summary.length,
                0
              );
              const totalTime = routeData.route.trip.legs.reduce(
                (sum, leg) => sum + leg.summary.time,
                0
              );
              const estimatedGallons = totalDistance / MAP_CONSTANTS.FUEL.AVERAGE_TRUCK_MPG;
              const totalTollCost = Number(routeData.tollCosts?.totalCost) || 0;
              const isTollCached = routeData.tollCosts?.cached || false;
              const cachedSegments = routeData.tollCosts?.cachedSegments || 0;
              const totalSegments = routeData.tollCosts?.totalSegments || 0;

              return (
                <div className="ll-route-info-grid">
                  <div className="ll-route-info-column">
                    <div className="ll-route-info-header">Drive Time</div>
                    <div className="ll-route-info-main">
                      {Math.floor(totalTime / 3600)}h {Math.floor((totalTime % 3600) / 60)}min
                    </div>
                  </div>
                  <div className="ll-route-info-column">
                    <div className="ll-route-info-header">Miles</div>
                    <div className="ll-route-info-main">{Math.round(totalDistance)}mi</div>
                  </div>
                  <div className="ll-route-info-column">
                    <div className="ll-route-info-header">Est. Gallons</div>
                    <div className="ll-route-info-main">{Math.round(estimatedGallons)}gal</div>
                  </div>
                  <div className="ll-route-info-column">
                    <div className="ll-route-info-header">Tolls</div>
                    <div className="ll-route-info-main">${totalTollCost.toFixed(2)}</div>
                    {isDeveloperUser() && totalSegments > 0 && (
                      <div className="ll-route-info-cache-indicator" title={`${cachedSegments}/${totalSegments} segments from cache`}>
                        {isTollCached ? '● cached' : `● ${cachedSegments}/${totalSegments} cached`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteControls;
